import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { ImperiaConfig, B2cConfig } from './authentication-oidc-configurations';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { FromProviderType } from '../interfaces/from-type-enum';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationOidcService {
  constructor(
    private oauthService: OAuthService,
    private router: Router,
  ) {
    this.setupTokenRefreshEventListeners();
  }
  private authState$ = new BehaviorSubject<boolean>(false);
  private userProfileSubject = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject.asObservable();
  private currentProvider: FromProviderType | null = null;
  private isHandlingRefreshError = false;

  /**
   * Observable that indicates the user's authentication state
   */
  public get authenticationState$(): Observable<boolean> {
    return this.authState$.asObservable();
  }

  /**
   * Setup event listeners for automatic token refresh monitoring
   */
  private setupTokenRefreshEventListeners(): void {
    this.oauthService.events.subscribe(event => {
      // Log all events in development mode
      if (!environment.PRODUCTION) {
        console.log('[OAuth Event]', event.type);
      }
      
      // Handle token refresh errors
      if (event.type === 'token_refresh_error') {
        console.error('❌ Token refresh error detected');
        this.handleTokenRefreshError();
      }
      
      // Log successful token operations
      if (event.type === 'token_received') {
        console.log('✅ Token received successfully');
      }
      
      if (event.type === 'silently_refreshed') {
        console.log('✅ Token silently refreshed');
      }
    });
  }

  /**
   * Handles token refresh errors by redirecting to unauthorized page
   * Avoids infinite loops by tracking if already handling an error
   */
  private handleTokenRefreshError(): void {
    if (this.isHandlingRefreshError) {
      console.warn('⚠️ Already handling refresh error, skipping');
      return;
    }
    
    this.isHandlingRefreshError = true;
    
    const storedProvider = sessionStorage.getItem(environment.PROVIDER_TYPE_STORAGE_KEY);
    console.log('🔄 Handling token refresh error. Provider:', storedProvider);
    
    // Don't try to auto-login, just redirect to unauthorized
    // The user can manually click to login from there
    this.authState$.next(false);
    this.router.navigate(['/unauthorized']).finally(() => {
      // Reset flag after navigation
      setTimeout(() => {
        this.isHandlingRefreshError = false;
      }, 1000);
    });
  }

  /**
   * Starts the login process with the specified provider
   */
  public async login(fromProviderType: FromProviderType | string): Promise<void> {
    if (!fromProviderType) return;
    
    sessionStorage.setItem(environment.PROVIDER_TYPE_STORAGE_KEY, fromProviderType);
    
    this.configure(fromProviderType as FromProviderType);
    try {
      await this.oauthService.loadDiscoveryDocument();
      await this.oauthService.tryLogin();

      if (this.oauthService.hasValidAccessToken()) {
        this.oauthService.setupAutomaticSilentRefresh();
        this.getUserData();
        this.authState$.next(true);
        return;
      }
      this.oauthService.initCodeFlow(undefined);
    } catch (error) {
      this.router.navigate(['/unauthorized']);
    }
  }

  /**
   * Logs out the user and clears authentication state
   */
  public logout(): void {
    this.oauthService.logOut();
    sessionStorage.removeItem(environment.PROVIDER_TYPE_STORAGE_KEY);
    this.authState$.next(false);
    this.userProfileSubject.next(null);
  }

  /**
   * Returns whether the user is authenticated
   */
  public isUserAuthenticated(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      observer.next(this.oauthService.hasValidAccessToken());
      observer.complete();
      this.authState$.next(this.oauthService.hasValidAccessToken());
    });
  }

  /**
   * Returns information about the authenticated user
   */
  public getUserInfo(): Observable<{ name: string; email: string }> {
    return new Observable((observer) => {
      const claims = this.oauthService.getIdentityClaims() as any;
      if (!claims) {
        observer.next({ name: '', email: '' });
        observer.complete();
        return;
      }
      const email = claims['email'] || (claims['emails'] && claims['emails'][0]) || '';
      const name = claims['given_name'] || claims['name'] || '';
      observer.next({ name, email });
      observer.complete();
    });
  }

  /**
   * Returns the access token of the authenticated user
   */
  public getBearerToken(): Observable<string> {
    return new Observable((observer) => {
      if (!this.oauthService.hasValidAccessToken()) {
        observer.next('');
        observer.complete();
        return;
      }
      observer.next(this.oauthService.getAccessToken() || '');
      observer.complete();
    });
  }

  /**
   * Configures the OAuthService for the desired provider
   */
  private configure(fromProviderType: FromProviderType) {
    this.currentProvider = fromProviderType;
    
    if (fromProviderType === FromProviderType.IMPERIA) {
      this.oauthService.configure(ImperiaConfig);
    } else if (fromProviderType === FromProviderType.SUITEEDGE) {
      this.oauthService.configure(B2cConfig);
    }
  }

  /**
   * Ensures the OAuth service is properly configured
   */
  private ensureConfigured(): void {
    if (!this.currentProvider) {
      const storedProvider = sessionStorage.getItem(environment.PROVIDER_TYPE_STORAGE_KEY);
      if (storedProvider) {
        this.currentProvider = storedProvider as FromProviderType;
      }
    }
    
    if (this.currentProvider && !this.oauthService.silentRefreshRedirectUri) {
      this.configure(this.currentProvider);
    }
  }

  /**
   * Extracts and updates the authenticated user's data
   */
  private getUserData() {
    const claims = this.oauthService.getIdentityClaims() as any;
    if (!claims) return;
    const email = claims['email'] || (claims['emails'] && claims['emails'][0]);
    this.userProfileSubject.next({
      email: email,
      nome: claims['given_name'] || claims['name'],
      tudo: claims,
    });
  }

  get isLoggedIn() {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Manually triggers a token refresh using refresh token
   * @returns Promise that resolves when refresh is complete
   * @throws Error if refresh fails or refresh token is not available
   */
  public async refreshToken(): Promise<void> {
    try {
      await this.ensureConfiguredAndLoadDiscovery();
      
      const hasRefreshToken = !!this.oauthService.getRefreshToken();
      
      if (!hasRefreshToken) {
        throw new Error('Refresh token not available');
      }
      
      await this.oauthService.refreshToken();
      
      this.getUserData();
      this.authState$.next(true);
      
    } catch (error: any) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }

  /**
   * Manually triggers a silent refresh
   * @returns Promise that resolves when silent refresh is complete
   * @throws Error if silent refresh fails
   */
  public async silentRefresh(): Promise<void> {
    try {
      await this.ensureConfiguredAndLoadDiscovery();
      
      await this.oauthService.silentRefresh();
      
      this.getUserData();
      this.authState$.next(true);
      
    } catch (error: any) {
      console.error('Error during silent refresh:', error);
      throw error;
    }
  }

  /**
   * Ensures OAuth is configured and discovery document is loaded
   */
  private async ensureConfiguredAndLoadDiscovery(): Promise<void> {
    // Ensure configuration is set
    if (!this.currentProvider) {
      const storedProvider = sessionStorage.getItem(environment.PROVIDER_TYPE_STORAGE_KEY);
      if (storedProvider) {
        this.currentProvider = storedProvider as FromProviderType;
      } else {
        throw new Error('Provider not configured. Please login again.');
      }
    }
    
    // Configure if not configured
    if (!this.oauthService.silentRefreshRedirectUri) {
      this.configure(this.currentProvider);
    }
    
    // Load discovery document if not loaded
    if (!this.oauthService.tokenEndpoint) {
      await this.oauthService.loadDiscoveryDocument();
    }
  }
}
